import { useState, useEffect } from "react";
import { ShieldCheck, Clock } from "lucide-react";
import styles from "./InsurancePricing.module.css";
import ToggleSwitch from "../../../components/BaseComponents/Toggle/ToggleSwitch";
import { useToast } from "../../../context/ToastContext";
import {
  useInsuranceConfig,
  useCreateInsuranceRule,
  useUpdateInsuranceRule,
  useDeleteInsuranceRule,
} from "../../../api/pricing/useInsurancePricingApi";
import DeleteConfirmModal from "../../../components/BaseComponents/DeleteConfirmModal/DeleteConfirmModal";
import { COUNTRIES } from "../../../constants/countries";

const TABS = [
  { key: "margin", label: "Margin Configuration" },
  { key: "validity", label: "Validity & Activation" },
  { key: "exclusions", label: "Country Exclusions" },
];

// ── Margin Configuration Tab ───────────────────────────────
const MarginTab = ({ config, loading, refetch, activeRuleId, hasActiveRule }) => {
  const { showToast } = useToast();
  const { execute: createRule, loading: createLoading } = useCreateInsuranceRule();
  const { execute: updateRule, loading: updateLoading } = useUpdateInsuranceRule();
  const { execute: deleteRule, loading: deleteLoading } = useDeleteInsuranceRule();
  const [form, setForm] = useState({
    insuranceName: "Standard Travel Protection",
    marketingText: "Comprehensive travel insurance covering cancellations, medical emergencies, and trip delays.",
    marginType: "Percentage",
    marginValue: 12,
    applyOn: "total",
    excludedCountries: [],
  });

  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

useEffect(() => {
  console.log('=== useEffect triggered ===');
  console.log('config received by component:', config);
  console.log('config type:', typeof config);
  console.log('config keys:', Object.keys(config || {}));
  
  if (!config) {
    return;
  }

  console.log('Full config object:', config);
  console.log('config.country_exclusions:', config.country_exclusions);
  const excluded = config.country_exclusions?.map((c) => c.country_code?.toUpperCase()) || [];
  console.log(`excluded : ${excluded}`)
  let applyOn = "total";
  const apiApply = config.margin_applied_on;

  if (apiApply === "FLIGHT_ONLY") applyOn = "flight";
  else if (apiApply === "HOTEL_ONLY") applyOn = "hotel";
  else applyOn = "total";

  const transformedConfig = {
    insuranceName: config.insurance_name || "",
    marketingText: config.insurance_description || "",
    marginType:
      config.marginType === "Percentage" ? "Percentage" : "Fixed",
    marginValue: config.marginValue || 0,
    applyOn,
    excludedCountries: excluded.length > 0 ? excluded : [],
    active: config.active,
    startDate: config.startDate,
    endDate: config.endDate,
  };

  console.log('Transformed config for form:', transformedConfig);
  console.log('insuranceName from config:', config.insurance_name);
  console.log('marketingText from config:', config.insurance_description);
  setForm(transformedConfig);
}, [config]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setIsSaving(true);
    try {
      console.log('=== Handle Save Called ===');
      console.log('Current form.excludedCountries:', form.excludedCountries);
      console.log('Form data being sent:', form);
      
      const payload = {};
      
      if (form.insuranceName !== (config?.insurance_name || "")) {
        payload.insuranceName = form.insuranceName;
      }
      if (form.marketingText !== (config?.insurance_description || "")) {
        payload.marketingText = form.marketingText;
      }
      
      // Always include margin fields to preserve existing values
      payload.marginType = form.marginType;
      payload.marginValue = form.marginValue;
      payload.applyOn = form.applyOn;
      
      // Always include excludedCountries - let useUpdateRule handle comparison logic
      payload.excludedCountries = form.excludedCountries;
      
      payload.active = config?.active ?? true;
      payload.startDate = config?.startDate ?? "";
      payload.endDate = config?.endDate ?? "";

      console.log('Final payload being sent to API:', payload);

      if (hasActiveRule && activeRuleId) {
        await updateRule(activeRuleId, payload);
        showToast("Insurance margin configuration saved", "success");
      } else {
        await createRule(payload);
        showToast("Insurance rule created successfully", "success");
      }
      await refetch();
    } catch (err) {
      showToast(err.response?.data?.message || err.response?.data?.errors?.[0] || "Failed to save", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!activeRuleId) return;
    try {
      await deleteRule(activeRuleId);
      showToast("Insurance rule deleted successfully", "success");
      setShowDeleteConfirm(false);
      await refetch();
    } catch (err) {
      showToast(err.response?.data?.message || err.response?.data?.errors?.[0] || "Failed to delete rule", "error");
    }
  };

  const saveDisabled = loading || createLoading || updateLoading || isSaving;

  const [searchQuery, setSearchQuery] = useState("");

  const toggleCountryExclusion = (code) => {
    const excludedCountries = form.excludedCountries || [];
    console.log('=== Toggle Country Exclusion ===');
    console.log('Country code:', code);
    console.log('Current excluded countries before toggle:', excludedCountries);
    console.log('Is country currently excluded?', excludedCountries.includes(code));
    
    if (excludedCountries.includes(code)) {
      const newExcluded = excludedCountries.filter(c => c !== code);
      console.log('Country removed. New excluded countries:', newExcluded);
      set("excludedCountries", newExcluded);
    } else {
      const newExcluded = [...excludedCountries, code];
      console.log('Country added. New excluded countries:', newExcluded);
      set("excludedCountries", newExcluded);
    }
  };

  const selectAllVisible = () => {
    const visibleCodes = filteredCountries.map(c => c.code);
    const excludedCountries = form.excludedCountries || [];
    console.log('=== Select All Visible ===');
    console.log('Visible country codes:', visibleCodes);
    console.log('Current excluded countries:', excludedCountries);
    console.log('Are all visible countries excluded?', visibleCodes.every(code => excludedCountries.includes(code)));
    
    if (!excludedCountries) return;
    const allSelected = visibleCodes.every(code => excludedCountries.includes(code));

    if (allSelected) {
      // Deselect all visible
      set("excludedCountries", excludedCountries.filter(code => !visibleCodes.includes(code)));
    } else {
      // Select all visible
      const newExcluded = [...new Set([...excludedCountries, ...visibleCodes])];
      set("excludedCountries", newExcluded);
    }
  };

  // Filter countries based on search query
  const filteredCountries = COUNTRIES.filter(country =>
    country.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    country.code.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const allVisibleSelected = filteredCountries.length > 0 &&
    filteredCountries.every(c => (form.excludedCountries || []).includes(c.code));

  return (
    <div className={styles.tableSection}>
      <div className={styles.configBlock}>
        {loading && (
          <div className={styles.loadingOverlay}>
            <span>Loading configuration…</span>
          </div>
        )}

        {!hasActiveRule && !loading && (
          <div className={styles.warningBar}>
            <span className={styles.warningIcon}>⚠</span>
            <span>No active insurance rule. Save to create one.</span>
          </div>
        )}

        {/* ── Insurance Details ──────────────────────────────── */}
        <div className={styles.configGroup}>
          <h4 className={styles.configTitle}>Insurance Product Details</h4>
          <p className={styles.configDesc}>
            Define the insurance product name and marketing description visible to customers.
          </p>

          <div className={styles.formGrid}>
            <div className={styles.field}>
              <label>Insurance Name</label>
              <input
                type="text"
                placeholder="e.g., Standard Travel Protection"
                value={form.insuranceName}
                onChange={(e) => set("insuranceName", e.target.value)}
                className={styles.textInput}
              />
            </div>
          </div>

          <div className={styles.field}>
            <label>Marketing Text</label>
            <textarea
              placeholder="Enter customer-facing description of insurance coverage..."
              value={form.marketingText}
              onChange={(e) => set("marketingText", e.target.value)}
              className={styles.textarea}
              rows={3}
            />
            <span className={styles.fieldHint}>
              This text will be displayed to customers during the booking process.
            </span>
          </div>
        </div>

        <div className={styles.divider} />

        {/* ── Margin Type ──────────────────────────────────── */}
        <div className={styles.configGroup}>
          <h4 className={styles.configTitle}>Margin Type</h4>
          <p className={styles.configDesc}>
            Choose whether the insurance margin is calculated as a percentage or a fixed amount.
          </p>
          <div className={styles.optionCards}>
            {[
              { value: "Percentage", label: "Percentage (%)", desc: "Margin is calculated as a percentage of the base amount." },
              { value: "Fixed", label: "Fixed Amount (€)", desc: "A fixed euro amount added on top of the insurance cost." },
            ].map((opt) => (
              <div
                key={opt.value}
                className={`${styles.optionCard} ${form.marginType === opt.value ? styles.optionCardActive : ""}`}
                onClick={() => set("marginType", opt.value)}
              >
                <label className={styles.radioItem}>
                  <input
                    type="radio"
                    name="marginType"
                    checked={form.marginType === opt.value}
                    onChange={() => set("marginType", opt.value)}
                  />
                </label>
                <div>
                  <strong>{opt.label}</strong>
                  <span>{opt.desc}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className={styles.divider} />

        {/* ── Margin Value ─────────────────────────────────── */}
        <div className={styles.configGroup}>
          <h4 className={styles.configTitle}>Margin Value</h4>
          <p className={styles.configDesc}>
            Enter the margin value to apply. This is a commercial margin only — fiscal interpretation is excluded.
          </p>
          <div className={styles.inputRow}>
            <div className={styles.inputWithAddon}>
              <span className={styles.addon}>
                {form.marginType === "Percentage" ? "%" : "€"}
              </span>
              <input
                type="number"
                min="0"
                step="0.01"
                placeholder={form.marginType === "Percentage" ? "e.g. 12" : "e.g. 25.00"}
                value={form.marginValue}
                onChange={(e) => set("marginValue", e.target.value)}
                className={styles.addonInput}
              />
            </div>
            {form.marginValue !== "" && (
              <span className={styles.previewChip}>
                Margin: {form.marginType === "Percentage" ? `${form.marginValue}%` : `€${form.marginValue}`}
              </span>
            )}
          </div>
        </div>

        <div className={styles.divider} />

        {/* ── Apply Margin On ───────────────────────────────── */}
        <div className={styles.configGroup}>
          <h4 className={styles.configTitle}>Apply Margin On</h4>
          <p className={styles.configDesc}>
            Define which sale price the insurance margin is applied against.
            Transfer is only a component inside a package, not a standalone product.
          </p>
          <div className={styles.optionCardsThree}>
            {[
              {
                value: "flight",
                label: "Flight Only",
                desc: "Apply margin on the flight sale price.",
                tag: "Flight Sale Price",
              },
              {
                value: "hotel",
                label: "Hotel Only",
                desc: "Apply margin on the hotel sale price.",
                tag: "Hotel Sale Price",
              },
              {
                value: "total",
                label: "Flight + Hotel (+ Transfer)",
                desc: "Apply margin on the total package price. Transfer is included as part of the package.",
                tag: "Total Package Price",
              },
            ].map((opt) => (
              <div
                key={opt.value}
                className={`${styles.optionCard} ${form.applyOn === opt.value ? styles.optionCardActive : ""}`}
                onClick={() => set("applyOn", opt.value)}
              >
                <label className={styles.radioItem}>
                  <input
                    type="radio"
                    name="applyOn"
                    checked={form.applyOn === opt.value}
                    onChange={() => set("applyOn", opt.value)}
                  />
                </label>
                <div>
                  <strong>{opt.label}</strong>
                  <span>{opt.desc}</span>
                  <span className={styles.applyTag}>{opt.tag}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className={styles.divider} />

        {/* ── Country Exclusions ────────────────────────────── */}
        <div className={styles.configGroup}>
          <h4 className={styles.configTitle}>Country Exclusions</h4>
          <p className={styles.configDesc}>
            Select countries where this insurance product is NOT applicable.
            Travelers to these destinations will not see this insurance option.
          </p>

          <div className={styles.countryExclusionBox}>
            <div className={styles.exclusionHeader}>
              <span className={styles.exclusionCount}>
                {(form.excludedCountries || []).length} {(form.excludedCountries || []).length === 1 ? 'country' : 'countries'} excluded
              </span>
              <div className={styles.headerActions}>
                {filteredCountries.length > 0 && (
                  <button
                    className={styles.selectAllBtn}
                    onClick={selectAllVisible}
                  >
                    {allVisibleSelected ? 'Deselect All Visible' : 'Select All Visible'}
                  </button>
                )}
                {(form.excludedCountries || []).length > 0 && (
                  <button
                    className={styles.clearAllBtn}
                    onClick={() => set("excludedCountries", [])}
                  >
                    Clear All
                  </button>
                )}
              </div>
            </div>

            {/* Search Input */}
            <div className={styles.searchWrapper}>
              <div className={styles.searchInputBox}>
                <svg className={styles.searchIcon} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.35-4.35" />
                </svg>
                <input
                  type="text"
                  placeholder="Search countries by name or code..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={styles.searchInput}
                />
                {searchQuery && (
                  <button
                    className={styles.clearSearchBtn}
                    onClick={() => setSearchQuery("")}
                    aria-label="Clear search"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                )}
              </div>
              {searchQuery && (
                <span className={styles.searchResultCount}>
                  {filteredCountries.length} {filteredCountries.length === 1 ? 'result' : 'results'}
                </span>
              )}
            </div>

            {/* Country Grid */}
            {filteredCountries.length > 0 ? (
              <div className={styles.countryGrid}>
                {filteredCountries.map((country) => {
                  const isExcluded = (form.excludedCountries || []).includes(country.code);
                  return (
                    <div
                      key={country.code}
                      className={`${styles.countryItem} ${isExcluded ? styles.countryItemExcluded : ""}`}
                      onClick={() => toggleCountryExclusion(country.code)}
                    >
                      <input
                        type="checkbox"
                        checked={isExcluded}
                        onChange={() => toggleCountryExclusion(country.code)}
                        className={styles.countryCheckbox}
                      />
                      <div className={styles.countryInfo}>
                        <span className={styles.countryFlag}>{country.code}</span>
                        <span className={styles.countryName}>{country.name}</span>
                      </div>
                      {isExcluded && (
                        <span className={styles.excludedBadge}>Excluded</span>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className={styles.noResults}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.35-4.35" />
                </svg>
                <p className={styles.noResultsText}>No countries found</p>
                <p className={styles.noResultsHint}>Try searching with a different keyword</p>
              </div>
            )}
          </div>
        </div>

        {/* ── Info Note ─────────────────────────────────────── */}
        <div className={styles.infoCard}>
          <ShieldCheck size={14} />
          <span>
            <strong>Commercial logic only.</strong> Any calculated difference here is commercial logic only.
            Fiscal interpretation is explicitly excluded.
          </span>
        </div>

        {/* ── Footer ───────────────────────────────────────── */}
        <div className={styles.configFooter}>
          <button className={styles.saveChangesBtn} onClick={handleSave} disabled={saveDisabled}>
            {saveDisabled ? "Saving…" : "Save Configuration"}
          </button>
          {hasActiveRule && (
            <button
              className={styles.deleteBtn}
              onClick={() => setShowDeleteConfirm(true)}
              disabled={deleteLoading}
            >
              {deleteLoading ? "Deleting…" : "Delete Current Rule"}
            </button>
          )}
        </div>

      </div>

      <DeleteConfirmModal
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        title="Delete insurance rule"
        itemName={config?.insurance_name || "Current insurance rule"}
        onConfirm={handleDelete}
        loading={deleteLoading}
      />
    </div>
  );
};

// ── Validity Tab ───────────────────────────────────────────
const ValidityTab = ({ config, loading, refetch, activeRuleId, hasActiveRule }) => {
  const { showToast } = useToast();
  const { execute: createRule, loading: createLoading } = useCreateInsuranceRule();
  const { execute: updateRule, loading: updateLoading } = useUpdateInsuranceRule();
  const [form, setForm] = useState(config ?? { active: true, startDate: "2025-01-01", endDate: "2025-12-31" });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (config) setForm(config);
  }, [config]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Only send fields that have changed from the original config
      const payload = {};
      
      // Compare with original config to determine what changed
      if (form.active !== config?.active) {
        payload.active = form.active;
      }
      if (form.startDate !== config?.startDate) {
        payload.startDate = form.startDate;
      }
      if (form.endDate !== config?.endDate) {
        payload.endDate = form.endDate;
      }
      
      // Always include margin fields to maintain the rule structure
      payload.marginType = config?.marginType ?? "Percentage";
      payload.marginValue = config?.marginValue ?? 12;
      payload.applyOn = config?.applyOn ?? "total";
      
      // Only include excludedCountries if it exists in config (don't send if unchanged)
      if (config?.country_exclusions) {
        payload.excludedCountries = config.country_exclusions.map(c => c.country_code);
      }

      if (hasActiveRule && activeRuleId) {
        await updateRule(activeRuleId, payload);
        showToast("Validity settings saved", "success");
      } else {
        await createRule(payload);
        showToast("Insurance rule created successfully", "success");
      }
      await refetch();
    } catch (err) {
      showToast(err.response?.data?.message || err.response?.data?.errors?.[0] || "Failed to save", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const saveDisabled = loading || createLoading || updateLoading || isSaving;

  return (
    <div className={styles.tableSection}>
      <div className={styles.configBlock}>
        {loading && (
          <div className={styles.loadingOverlay}>
            <span>Loading configuration…</span>
          </div>
        )}

        {!hasActiveRule && !loading && (
          <div className={styles.warningBar}>
            <span className={styles.warningIcon}>⚠</span>
            <span>No active insurance rule. Save to create one.</span>
          </div>
        )}

        <h4 className={styles.configTitle}>Validity & Activation</h4>
        <p className={styles.configDesc}>
          Control when the insurance pricing configuration is active. Outside the validity period, these rules will not be applied.
        </p>

        <div className={styles.formGrid}>
          <div className={styles.field}>
            <label>Start Date</label>
            <input
              type="date"
              value={form.startDate}
              onChange={(e) => set("startDate", e.target.value)}
            />
          </div>
          <div className={styles.field}>
            <label>End Date</label>
            <input
              type="date"
              value={form.endDate}
              onChange={(e) => set("endDate", e.target.value)}
            />
          </div>
        </div>

        <div className={styles.toggleField}>
          <div>
            <strong>Active Status</strong>
            <p>When inactive, insurance pricing margins will not be applied to any bookings.</p>
          </div>
          <div className={styles.toggleWrapper}>
            <span className={form.active ? styles.toggleLabelOn : styles.toggleLabelOff}>
              {form.active ? "Active" : "Inactive"}
            </span>
            <ToggleSwitch
              checked={form.active}
              onChange={() => set("active", !form.active)}
            />
          </div>
        </div>

        <div className={styles.infoCard}>
          <Clock size={14} />
          <span>
            <strong>Note:</strong> Insurance pricing rules outside their validity period are never applied,
            regardless of their active status.
          </span>
        </div>

        <div className={styles.configFooter}>
          <button
            className={styles.saveChangesBtn}
            onClick={handleSave}
            disabled={saveDisabled}
          >
            {saveDisabled ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Country Exclusions Tab ───────────────────────────────────
const ExclusionsTab = ({ config, loading, refetch, activeRuleId, hasActiveRule }) => {
  const { showToast } = useToast();
  const { execute: createRule, loading: createLoading } = useCreateInsuranceRule();
  const { execute: updateRule, loading: updateLoading } = useUpdateInsuranceRule();
  const [form, setForm] = useState({ excludedCountries: [] });
  const [isSaving, setIsSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (config?.country_exclusions) {
      const excluded = config.country_exclusions.map((c) => c.country_code?.toUpperCase()) || [];
      setForm({ excludedCountries: excluded });
    }
  }, [config]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const payload = {
        country_exclusions: form.excludedCountries.map(code => {
          const country = COUNTRIES.find(c => c.code === code);
          return {
            country_code: code,
            country_name: country ? country.name : code
          };
        })
      };

      if (hasActiveRule && activeRuleId) {
        await updateRule(activeRuleId, payload);
        showToast("Country exclusions updated", "success");
      } else {
        await createRule(payload);
        showToast("Insurance rule created with exclusions", "success");
      }
      await refetch();
    } catch (err) {
      showToast(err.response?.data?.message || err.response?.data?.errors?.[0] || "Failed to save", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const toggleCountryExclusion = (code) => {
    const excludedCountries = form.excludedCountries || [];
    if (excludedCountries.includes(code)) {
      const newExcluded = excludedCountries.filter(c => c !== code);
      set("excludedCountries", newExcluded);
    } else {
      const newExcluded = [...excludedCountries, code];
      set("excludedCountries", newExcluded);
    }
  };

  const filteredCountries = COUNTRIES.filter(country =>
    country.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    country.code.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const saveDisabled = loading || createLoading || updateLoading || isSaving;

  return (
    <div className={styles.tableSection}>
      <div className={styles.configBlock}>
        {loading && (
          <div className={styles.loadingOverlay}>
            <span>Loading configuration…</span>
          </div>
        )}

        <div className={styles.configGroup}>
          <h4 className={styles.configTitle}>Country Exclusions</h4>
          <p className={styles.configDesc}>
            Select countries where insurance pricing should not be applied.
          </p>

          <div className={styles.field}>
            <label>Search Countries</label>
            <input
              type="text"
              placeholder="Search by country name or code..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={styles.textInput}
            />
          </div>

          {filteredCountries.length > 0 ? (
            <div className={styles.countryGrid}>
              {filteredCountries.map((country) => {
                const isExcluded = (form.excludedCountries || []).includes(country.code);
                return (
                  <div
                    key={country.code}
                    className={`${styles.countryChip} ${isExcluded ? styles.countryChipExcluded : ""}`}
                    onClick={() => toggleCountryExclusion(country.code)}
                  >
                    <span>{country.name}</span>
                    <span className={styles.countryCode}>{country.code}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className={styles.noResults}>
              No countries found matching "{searchQuery}"
            </div>
          )}
        </div>

        <div className={styles.configFooter}>
          <button
            className={styles.saveChangesBtn}
            onClick={handleSave}
            disabled={saveDisabled}
          >
            {isSaving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

const APPLY_ON_LABELS = { flight: "Flight Only", hotel: "Hotel Only", total: "Total Package" };

// ── Main Page ──────────────────────────────────────────────
const InsurancePricing = () => {
  const [activeTab, setActiveTab] = useState("margin");
  const { data: config, loading, refetch, activeRuleId, hasActiveRule } = useInsuranceConfig();

  const marginConfig = config ? { 
    insurance_name: config.insurance_name,
    insurance_description: config.insurance_description,
    marginType: config.marginType, 
    marginValue: config.marginValue, 
    applyOn: config.applyOn, 
    active: config.active, 
    startDate: config.startDate, 
    endDate: config.endDate,
    country_exclusions: config.country_exclusions
  } : null;
  const validityConfig = config ? { 
    active: config.active, 
    startDate: config.startDate, 
    endDate: config.endDate, 
    marginType: config.marginType, 
    marginValue: config.marginValue, 
    applyOn: config.applyOn,
    country_exclusions: config.country_exclusions
  } : null;

  return (
    <div className={styles.container}>

      <div className={styles.toolbar}>
        <div className={styles.manager}>Insurance Pricing & Margin Logic</div>
      </div>

      <div className={styles.warningBar}>
        <span className={styles.warningIcon}>⚠</span>
        <span>
          Insurance margin rules are <strong>commercial logic only</strong>.
          Fiscal interpretation is explicitly excluded from this configuration.
        </span>
      </div>

      {/* Summary Cards */}
      <div className={styles.statsRow}>
        <div className={styles.statCard}>
          <span className={styles.statNum}>{loading ? "…" : "Commercial"}</span>
          <span className={styles.statLabel}>Logic Type</span>
        </div>
        <div className={styles.statCard}>
          <span className={`${styles.statNum} ${loading ? "" : config?.active ? styles.statGreen : styles.statGray}`}>
            {loading ? "…" : config?.active ? "Active" : "Inactive"}
          </span>
          <span className={styles.statLabel}>Current Status</span>
        </div>
        <div className={styles.statCard}>
          <span className={styles.statNum}>
            {loading ? "…" : config?.marginType === "Percentage" ? `${config?.marginValue ?? 0}%` : `€${config?.marginValue ?? 0}`}
          </span>
          <span className={styles.statLabel}>Current Margin</span>
        </div>
        <div className={styles.statCard}>
          <span className={styles.statNum}>
            {loading ? "…" : APPLY_ON_LABELS[config?.applyOn] ?? "—"}
          </span>
          <span className={styles.statLabel}>Applied On</span>
        </div>
      </div>

      {/* Tabs */}
      <div className={styles.tabs}>
        {TABS.map((tab) => (
          <button
            key={tab.key}
            className={`${styles.tab} ${activeTab === tab.key ? styles.tabActive : ""}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "margin" && (
        <MarginTab
          config={marginConfig}
          loading={loading}
          refetch={refetch}
          activeRuleId={activeRuleId}
          hasActiveRule={hasActiveRule}
        />
      )}
      {activeTab === "validity" && (
        <ValidityTab
          config={validityConfig}
          loading={loading}
          refetch={refetch}
          activeRuleId={activeRuleId}
          hasActiveRule={hasActiveRule}
        />
      )}
      {activeTab === "exclusions" && (
        <ExclusionsTab
          config={validityConfig}
          loading={loading}
          refetch={refetch}
          activeRuleId={activeRuleId}
          hasActiveRule={hasActiveRule}
        />
      )}

    </div>
  );
};

export default InsurancePricing;